import type {
  BillCounterConfig,
  BillCounterStatus,
  CounterData,
  CounterDataEvent,
  CounterStatusEvent,
  CounterErrorEvent,
} from '@abcpay/shared'
import { DEFAULT_COUNTER_CONFIG } from '@abcpay/shared'
import { bridge, BridgeNotAvailableError } from '../bridge'
import {
  loadCounterSettings as loadFromStorage,
  saveCounterSettings as saveToStorage,
} from '../storage'

type SaveResult = { success: boolean; message: string }

export async function loadCounterSettings(): Promise<BillCounterConfig> {
  try {
    if (bridge.isElectron) {
      const result = await bridge.getCounterSettings()
      await saveToStorage(result.config)
      return result.config
    }
    return await loadFromStorage()
  } catch (error) {
    console.error('계수기 설정 로드 실패:', error)
    return DEFAULT_COUNTER_CONFIG
  }
}

export async function saveCounterSettings(config: BillCounterConfig): Promise<SaveResult> {
  try {
    if (bridge.isElectron) {
      const result = await bridge.saveCounterSettings({ config })
      if (result.success) {
        await saveToStorage(config)
      }
      return {
        success: result.success,
        message: result.message,
      }
    }

    await saveToStorage(config)
    return {
      success: true,
      message: '계수기 설정이 저장되었습니다.',
    }
  } catch (error) {
    return {
      success: false,
      message: `설정 저장 실패: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
}

export async function startCounter(config: BillCounterConfig): Promise<BillCounterStatus> {
  try {
    if (!bridge.isElectron) {
      return {
        connected: false,
        port: config.serialPort,
        error: '계수기 시작은 POS 앱(Electron)에서만 가능합니다.',
      }
    }
    return await bridge.startCounter({ config })
  } catch (error) {
    return {
      connected: false,
      port: config.serialPort,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function stopCounter(): Promise<void> {
  if (!bridge.isElectron) return
  try {
    await bridge.stopCounter()
  } catch (error) {
    console.error('지폐계수기 중지 실패:', error)
  }
}

export async function testCounter(config: BillCounterConfig): Promise<BillCounterStatus> {
  try {
    if (!bridge.isElectron) {
      return {
        connected: false,
        port: config.serialPort,
        error: '계수기 테스트는 POS 앱(Electron)에서만 가능합니다.',
      }
    }
    return await bridge.testCounter({ config })
  } catch (error) {
    return {
      connected: false,
      port: config.serialPort,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function getCounterStatus(): Promise<BillCounterStatus> {
  try {
    if (!bridge.isElectron) {
      return {
        connected: false,
        port: '',
      }
    }
    return await bridge.getCounterStatus()
  } catch (error) {
    if (error instanceof BridgeNotAvailableError) {
      return {
        connected: false,
        port: '',
      }
    }
    throw error
  }
}

function parseCounterData(payload: unknown): CounterData | null {
  if (!payload || typeof payload !== 'object') return null
  if ('data' in payload) {
    return (payload as CounterDataEvent).data
  }
  if ('currency' in payload && 'totalAmount' in payload && 'denominations' in payload) {
    return payload as CounterData
  }
  return null
}

function parseCounterStatus(payload: unknown): BillCounterStatus | null {
  if (!payload || typeof payload !== 'object') return null
  if ('status' in payload) {
    return (payload as CounterStatusEvent).status
  }
  if ('connected' in payload && 'port' in payload) {
    return payload as BillCounterStatus
  }
  return null
}

function parseCounterError(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  if ('message' in payload) {
    return (payload as CounterErrorEvent).message
  }
  return null
}

export function onCounterData(callback: (data: CounterData) => void): () => void {
  if (!bridge.isElectron) return () => {}
  return bridge.on('counter:data', (payload) => {
    const data = parseCounterData(payload)
    if (data) callback(data)
  })
}

export function onCounterStatus(callback: (status: BillCounterStatus) => void): () => void {
  if (!bridge.isElectron) return () => {}
  return bridge.on('counter:status', (payload) => {
    const status = parseCounterStatus(payload)
    if (status) callback(status)
  })
}

export function onCounterError(callback: (message: string) => void): () => void {
  if (!bridge.isElectron) return () => {}
  return bridge.on('counter:error', (payload) => {
    const message = parseCounterError(payload)
    if (message) callback(message)
  })
}
